import * as React from 'react';
import AnimateHeight from 'react-animate-height';
import Creatable from 'react-select/creatable';
import Button from 'src/components/Button';
import EmailLists from 'src/components/EmailLists';
import Label from 'src/components/FormElements/Label';
import Icon from 'src/components/Icon';
import { getEmailLists } from 'src/helpers/api';
import { ValidationError } from 'src/shared/errors';
import { EmailList } from 'src/types/EmailLists';

const EMAIL_PATTERN = /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;

type SendFormProps = {
  isExpanded: boolean;
  onSubmit: (emails: string[]) => any;
};

// Parse string containing comma-separated emails or group ids
const parseEmails = (emails: string[]): string[] | ValidationError => {
  if (!emails.length) {
    return new ValidationError('filled');
  }

  for (const email of emails) {
    const isValid = EMAIL_PATTERN.test(email) || isNaN(parseInt(email, 10)) === false;
    if (isValid === false) {
      return new ValidationError('email');
    }
  }

  return emails;
};

const SendForm = ({ isExpanded, onSubmit }: SendFormProps) => {
  const [lists, setLists] = React.useState<EmailList[]>([]);
  const [emails, setEmails] = React.useState<string[]>([]);
  const [error, setError] = React.useState<ValidationError | null>(null);
  const [isEmailListsVisible, setIsEmailListsVisible] = React.useState<boolean>(false);

  const fetchEmailLists = async () => {
    setLists(await getEmailLists());
  };

  React.useEffect(() => {
    fetchEmailLists();
  }, []);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const result = parseEmails(emails);

    if (result instanceof ValidationError) {
      setError(result);
    } else {
      setError(null);
      onSubmit(result);
    }
  };

  return (
    <AnimateHeight
      height={isExpanded ? 'auto' : 0}
    >
      <EmailLists
        isOpen={isEmailListsVisible}
        onClose={() => {
          setIsEmailListsVisible(false);
          fetchEmailLists();
        }}
      />
      <form
        className="mt-4 pt-3 border-t border-dark-gray-body flex items-start"
        onSubmit={handleSubmit}
      >
        <div className="w-full">
          <Label htmlFor="email" className="h-6">Indtast din e-mailadresse</Label>
          <Creatable
            isMulti={true}
            options={lists.map(({ id, name }) => ({ value: id, label: name }))}
            formatCreateLabel={(input: string) => `Tilføj ${input}`}
            isValidNewOption={(input: string) => EMAIL_PATTERN.test(input)}
            noOptionsMessage={() => 'Ingen emailgrupper fundet'}
            onChange={(value: Array<{ value: string }>) => {
              const nextEmails = value.map((option) => option.value);
              setEmails(nextEmails);
            }}
            placeholder="Indtast e-mailadresser…"
            styles={{
              control: (provided) => ({ ...provided, minHeight: 43 }),
            }}
          />
          {error && (
            <p className="mt-1 mb-0 font-semibold has-text-danger">{error.message}</p>
          )}
          <button type="button" onClick={() => setIsEmailListsVisible(true)}>
            <p className="h-6 f-7 mt-1 mb-0 has-text-gray-600 flex items-center cursor-pointer">
              Opret og rediger emailgrupper her
              <Icon name="pencil" className="ml-1 w-4 h-4" />
            </p>
          </button>
        </div>
        <Button size="large" className="mt-6 mb-8 flex-none ml-2">
          Send
          <Icon name="chevron-right" className="ml-2 -mr-1" />
        </Button>
      </form>
    </AnimateHeight>
  );
};

export default SendForm;



// WEBPACK FOOTER //
// ./src/Pages/CoursePortal/Reports/components/SendForm.tsx